import { useCallback, useEffect, useRef, useState } from 'react';
import { getErrorMessage } from '../api/client';
import { getWasteReport } from '../api/waste';
import type { WasteReport, WasteReportGroup } from '../types/waste';
import { defaultRange, localDateString, MAX_WASTE_RANGE_DAYS, rangeProblem, reportSummaryText } from '../utils/wasteReport';
import { formatWasteQuantity } from '../utils/wasteReview';
import { PeriodField } from './period/PeriodPicker';
import { LoadingPanel } from './ui/primitives';

// F2 · reporte de mermas de un rango: por categoría, por empleado, por turno y por insumo. Solo cuenta
// lo vigente; lo anulado va aparte. El valor se ve como en Inventario (decisión del dueño en F1).

const money = (value: string) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(value));

export function WasteReportPanel({ stores, multiStore, refreshKey, timeZone }: {
  stores: Array<{ id: string; name: string }>;
  multiStore: boolean;
  /** Cambia cuando una acción de la pantalla (anular) movió las cifras. */
  refreshKey: number;
  /** Zona de la tienda para las fechas por defecto (no la del teléfono). */
  timeZone: string;
}) {
  const [range, setRange] = useState(() => defaultRange(30, new Date(), timeZone));
  const [storeId, setStoreId] = useState('');
  const [report, setReport] = useState<WasteReport>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const loadSeq = useRef(0);
  const problem = rangeProblem(range.dateFrom, range.dateTo);

  const load = useCallback(async () => {
    if (problem) return;
    const current = ++loadSeq.current;
    setLoading(true);
    setError(undefined);
    try {
      const result = await getWasteReport({ dateFrom: range.dateFrom, dateTo: range.dateTo, storeId: storeId || undefined });
      if (current === loadSeq.current) setReport(result);
    } catch (loadError) {
      if (current === loadSeq.current) setError(getErrorMessage(loadError, 'No fue posible cargar el reporte de mermas.'));
    } finally {
      if (current === loadSeq.current) setLoading(false);
    }
  }, [problem, range.dateFrom, range.dateTo, storeId]);

  useEffect(() => {
    void load();
    return () => { loadSeq.current += 1; };
  }, [load, refreshKey]);

  return (
    <section className="content-card" aria-label="Reporte de mermas">
      <div className="content-card__header">
        <div>
          <span className="eyebrow">Reporte</span>
          <h2>Reporte de mermas</h2>
          <p className="wiz-hint">Lo que se perdió en estas fechas. Solo cuenta lo que no se anuló.</p>
        </div>
      </div>
      <div className="stock-filters">
        {/* P-075: el selector global de periodos en vez de Desde / Hasta. Siempre un rango, con el «hoy» de la
            tienda y el tope del servidor (ver WasteReviewPanel). Se aplica al elegir, como antes. */}
        <PeriodField
          allowAll={false}
          allowFuture
          className="date-range"
          label="Fechas"
          maxDays={MAX_WASTE_RANGE_DAYS + 1}
          onChange={(next) => setRange({ dateFrom: next.from, dateTo: next.to })}
          today={localDateString(new Date(), timeZone)}
          value={{ from: range.dateFrom, to: range.dateTo }}
        />
        {multiStore && (
          <label className="field field--inline"><span>Tienda</span>
            <select onChange={(event) => setStoreId(event.target.value)} value={storeId}>
              <option value="">Todas mis tiendas</option>
              {stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
            </select>
          </label>
        )}
      </div>
      {problem && <p className="wiz-hint" role="alert">{problem}</p>}
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {loading && (report ? <div className="inline-loading"><span className="spinner" />Actualizando…</div> : <LoadingPanel label="Cargando el reporte de mermas…" />)}
      {report && <>
        <dl className="detail-list">
          <div><dt>Reportada por el equipo</dt><dd>{reportSummaryText(report.totals.reported)}</dd></div>
          <div><dt>Detectada en conteo</dt><dd>{reportSummaryText(report.totals.detectedInCount)}</dd></div>
          <div><dt>Anulada</dt><dd>{reportSummaryText(report.totals.reversed)}</dd></div>
        </dl>
        {report.partial && <p className="wiz-hint">Cifras parciales: estas fechas tienen más de 20,000 mermas. Acorta el rango.</p>}
        <WasteReportTable column="Categoría" groups={report.byCategory} title="Por categoría" />
        <WasteReportTable column="Quién la registró" groups={report.byEmployee} title="Por empleado" />
        <WasteReportTable column="Corte" groups={report.byShift} title="Por turno" />
        <WasteReportTable column="Insumo" groups={report.byIngredient} title="Por insumo" withQuantity />
        <p className="wiz-hint">La merma detectada en conteo la registra quien contó, por eso no entra en «por empleado» ni en «por turno». El turno es el corte cerrado que cubre esa hora; si ninguno la cubre, dice «Sin corte».</p>
      </>}
    </section>
  );
}

function WasteReportTable({ title, column, groups, withQuantity = false }: { title: string; column: string; groups: WasteReportGroup[]; withQuantity?: boolean }) {
  return (
    <div className="waste-report-group">
      <h3>{title}</h3>
      {groups.length === 0
        ? <p className="wiz-hint">Sin mermas en estas fechas.</p>
        : <div className="data-table-wrap">
          {/* P-076: tabla analítica compacta (se comparan grupos): identidad fija y desplazamiento local en el teléfono. */}
          <table className="data-table n3-table--analytic">
            <thead><tr><th>{column}</th>{withQuantity && <th>Cantidad</th>}<th>Mermas</th><th>Valor</th></tr></thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.key}>
                  <td>{group.label}</td>
                  {withQuantity && <td>{group.quantity && group.unitSymbol ? formatWasteQuantity(group.quantity, group.unitSymbol) : '—'}</td>}
                  <td>{group.count}</td>
                  <td>{money(group.value)}{group.uncostedCount ? ` · ${group.uncostedCount} sin costo` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
    </div>
  );
}
